/**
 * API functions for video share endpoints
 */

import { apiClient } from "./apiClient";

export interface VideoShareData {
  video: {
    id: string;
    project_id: string;
    s3_path: string | null;
    download_url: string | null;
    file_size: number;
    storage_provider: string;
    bunny_video_id: string | null;
    encoding_status: string | null;
    encoding_progress: number | null;
    thumbnail_url: string | null;
    preview_url: string | null;
    duration: number | null;
    width: number | null;
    height: number | null;
    available_resolutions: string | null;
    embed_url: string | null;
  };
  project: {
    name: string;
    logo_url: string | null;
    banner_url: string | null;
    background_url: string | null;
    description: string | null;
    colors: {
      form_background: string;
      text_color: string;
      button_gradient_start: string;
      button_gradient_end: string;
      button_text_color: string;
    } | null;
  };
  recipient: {
    name: string | null;
    email: string;
  };
  analytics: {
    total_views: number;
    total_downloads: number;
    total_shares: number;
  };
}

export type AnalyticsEventType =
  | "page_view"
  | "video_download"
  | "video_share";

export interface TrackEventRequest {
  event_type: AnalyticsEventType;
  has_consent: boolean;
  user_agent?: string;
  referer?: string;
}

/**
 * Fetch video share data by short key
 * Public endpoint - no auth required
 */
export async function fetchVideoShare(key: string): Promise<VideoShareData> {
  return apiClient<VideoShareData>(`/v/${key}`, { requiresAuth: false });
}

/**
 * Track an analytics event
 * Public endpoint - no auth required
 */
export async function trackEvent(
  key: string,
  eventData: TrackEventRequest
): Promise<{ success: boolean; message: string }> {
  return apiClient<{ success: boolean; message: string }>(`/v/${key}/track`, {
    method: "POST",
    body: JSON.stringify(eventData),
    requiresAuth: false,
  });
}

/**
 * Get analytics summary for a share link
 * Public endpoint - no auth required
 */
export async function fetchAnalytics(
  key: string
): Promise<VideoShareData["analytics"]> {
  return apiClient<VideoShareData["analytics"]>(`/v/${key}/analytics`, {
    requiresAuth: false,
  });
}
